import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import GanttTaskPanel from './GanttTaskPanel';
import GanttTimeline from './GanttTimeline';
import {
    startOfDay, addDays, diffDays, parseDate, toISODate, flattenTasks,
    computeRange, validateTaskDates, MODE_CONFIG, MOCK_TASKS, DEFAULT_STATUS_COLORS, t
} from './GanttUtils';
import './index.scss';

// ─── Mode labels ─────────────────────────────────────────────────
var MODE_LABELS = [
    { key: 'day', label: 'D', title: { vi: 'Theo ngày', en: 'Day', fr: 'Par jour', es: 'A diario', id: 'Sehari-hari', ja: '毎日', zh: '日常的', ko: '일일', hi: 'दैनिक', ar: 'يوميًا' } },
    { key: 'week', label: 'W', title: { vi: 'Theo tuần', en: 'Week', fr: 'Par semaine', es: 'Por semana', id: 'Mingguan', ja: '週別', zh: '按周', ko: '주별', hi: 'साप्ताहिक', ar: 'أسبوعي' } },
    { key: 'month', label: 'M', title: { vi: 'Theo tháng', en: 'Month', fr: 'Par mois', es: 'Por mes', id: 'Bulanan', ja: '月別', zh: '按月', ko: '월별', hi: 'मासिक', ar: 'شهري' } },
    { key: 'quarter', label: 'Q', title: { vi: 'Theo quý', en: 'Quarter', fr: 'Trimestre', es: 'Trimestre', id: 'Kuartalan', ja: '四半期別', zh: '按季度', ko: '분기별', hi: 'तिमाही', ar: 'ربع سنوي' } },
    { key: 'year', label: 'Y', title: { vi: 'Theo năm', en: 'Year', fr: 'Par an', es: 'Por año', id: 'Tahunan', ja: '年別', zh: '按年', ko: '연도별', hi: 'वार्षिक', ar: 'سنوي' } }
];

// ─── Context menu actions ────────────────────────────────────────
var DEFAULT_CONTEXT_ACTIONS = [
    { key: 'view', label: { vi: 'Xem chi tiết', en: 'View Detail', fr: 'Voir le détail', es: 'Ver detalle', id: 'Lihat detail', ja: '詳細を表示', zh: '查看详情', ko: '상세 보기', hi: 'विवरण देखें', ar: 'عرض التفاصيل' }, icon: 'dx-icon-find' },
    { key: 'edit', label: { vi: 'Sửa', en: 'Edit', fr: 'Modif.', es: 'Editar', id: 'Ubah', ja: '編集', zh: '编辑', ko: '수정', hi: 'संपादन', ar: 'تعديل' }, icon: 'dx-icon-edit', requireEditable: true },
    { key: 'create_child', label: { vi: 'Tạo task con', en: 'Create Sub-task', fr: 'Créer sous-tâche', es: 'Crear subtarea', id: 'Buat subtugas', ja: 'サブタスク作成', zh: '创建子任务', ko: '하위 작업 생성', hi: 'उप-कार्य बनाएँ', ar: 'إنشاء مهمة فرعية' }, icon: 'dx-icon-add', requireEditable: true },
    { key: 'separator' },
    { key: 'delete', label: { vi: 'Xoá', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' }, icon: 'dx-icon-trash', requireEditable: true, danger: true }
];

// ═════════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ─────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════
export default forwardRef(function AntGanttChart(props, ref) {
    var {
        // ─── Data source ────────────────────────────
        API, session, items: itemsProp, searchConditions,

        // ─── Field mapping ──────────────────────────
        idField = 'id', nameField = 'name', startField = 'start', endField = 'end',
        progressField = 'progress', statusField = 'status', parentField = 'parent',
        assigneeField = 'assignee', dependencyField = 'dependencies',

        // ─── Display ────────────────────────────────
        mode: modeProp = 'week',
        startDate: startDateProp, endDate: endDateProp,
        showProgress = true, showDependencies = true, showMilestones = true,
        showToday = true, compactRows = false, height = 500,
        statusColors: statusColorsProp,

        // ─── Left panel columns ─────────────────────
        panelColumns,

        // ─── Toolbar config ─────────────────────────
        toolbar: toolbarConfig,

        // ─── Interaction ────────────────────────────
        onTaskClick, onTaskDoubleClick,
        editable = false,

        // ─── CRUD callbacks ─────────────────────────
        onCreate, onUpdate, onDelete,

        // ─── Drilldown ──────────────────────────────
        detailUrl,
        detailField = 'code',
        navigate: navigateProp,

        // ─── Context menu ───────────────────────────
        contextActions: contextActionsProp,
        onContextAction,

        // ─── Condition & Mock ───────────────────────
        mockMode = false,
        visible,

        // ─── Visibility conditions per action ───────
        createVisible, editVisible, deleteVisible
    } = props;

    // ─── All hooks MUST be called before any conditional return ──
    var navigateHook = useNavigate();
    var [rawTasks, setRawTasks] = useState([]);
    var [mode, setMode] = useState(modeProp);
    var [expandedGroups, setExpandedGroups] = useState({});
    var [loading, setLoading] = useState(false);
    var [selectedTaskId, setSelectedTaskId] = useState(null);
    var [contextMenu, setContextMenu] = useState(null);

    var timelineScrollRef = useRef(null);
    var taskPanelRef = useRef(null);
    var rowHeight = compactRows ? 30 : 44;

    // ─── Visibility check (AFTER all hooks) ─────────────────────
    var isHidden = visible && session && !AntCondition.checkAll({}, visible, session);

    var statusColors = useMemo(function () {
        return { ...DEFAULT_STATUS_COLORS, ...statusColorsProp };
    }, [statusColorsProp]);

    // ─── Permission checks via AntCondition ──────────────────────
    var canCreate = useMemo(function () {
        if (!editable) return false;
        if (!createVisible) return true;
        return AntCondition.checkAll({}, createVisible, session);
    }, [editable, createVisible, session]);

    var canEditTask = useCallback(function (task) {
        if (!editable) return false;
        if (!editVisible) return true;
        return AntCondition.checkAll(task || {}, editVisible, session);
    }, [editable, editVisible, session]);

    var canDeleteTask = useCallback(function (task) {
        if (!editable) return false;
        if (!deleteVisible) return true;
        return AntCondition.checkAll(task || {}, deleteVisible, session);
    }, [editable, deleteVisible, session]);

    // ═════════════════════════════════════════════════════════════
    // ─── DATA LOADING ───────────────────────────────────────────
    // ═════════════════════════════════════════════════════════════
    var loadData = useCallback(async function () {
        if (mockMode) { setRawTasks(MOCK_TASKS); return; }
        if (itemsProp) { setRawTasks(itemsProp); return; }
        if (!API) return;

        setLoading(true);
        try {
            var params = { pageSize: 1000, ...(searchConditions || {}) };
            var result = await API.getList(params);
            var rows = (result && result.rows) || (result && result.data && result.data.rows) || [];
            setRawTasks(rows);
        } catch (e) { /* silent */ }
        setLoading(false);
    }, [API, searchConditions, itemsProp, mockMode]);

    useEffect(function () { loadData(); }, [loadData]);

    // ═════════════════════════════════════════════════════════════
    // ─── CRUD OPERATIONS ────────────────────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleCreate = useCallback(async function (defaults) {
        if (onCreate) { onCreate(defaults); return; }
        if (!API || mockMode) return;
        try {
            var prepareResult = await API.postCreatePrepare(defaults || {});
            // Caller should handle popup with prepareResult
            return prepareResult;
        } catch (e) { /* silent */ }
    }, [API, mockMode, onCreate]);

    var handleUpdate = useCallback(async function (task, changes) {
        if (onUpdate) { onUpdate(task, changes); return; }
        if (!API || mockMode) {
            // Optimistic update for mock
            setRawTasks(function (prev) {
                return prev.map(function (t) {
                    if (t.id === task.id) return { ...t, ...changes };
                    return t;
                });
            });
            return;
        }
        try {
            await API.postUpdate(task, changes);
            loadData(); // Reload after update
        } catch (e) { loadData(); }
    }, [API, mockMode, onUpdate, loadData]);

    var handleDelete = useCallback(async function (task) {
        if (onDelete) { onDelete(task); return; }
        if (!API || mockMode) {
            setRawTasks(function (prev) { return prev.filter(function (t) { return t.id !== task.id; }); });
            return;
        }
        try {
            await API.postDelete([task]);
            loadData();
        } catch (e) { loadData(); }
    }, [API, mockMode, onDelete, loadData]);

    // ═════════════════════════════════════════════════════════════
    // ─── DRAG OPERATIONS (move & resize) ────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleDragMove = useCallback(function (task, newStart, newEnd, daysDelta) {
        if (!canEditTask(task)) return;
        var changes = {};
        changes[startField] = newStart;
        changes[endField] = newEnd;
        handleUpdate(task, changes);
    }, [canEditTask, startField, endField, handleUpdate]);

    var handleDragResize = useCallback(function (task, newStart, newEnd, daysDelta) {
        if (!canEditTask(task)) return;
        var v = validateTaskDates(parseDate(newStart), parseDate(newEnd));
        if (!v.valid) return;
        var changes = {};
        changes[startField] = newStart;
        changes[endField] = newEnd;
        handleUpdate(task, changes);
    }, [canEditTask, startField, endField, handleUpdate]);

    // ═════════════════════════════════════════════════════════════
    // ─── EMPTY SLOT CLICK → CREATE ──────────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleEmptySlotClick = useCallback(function (dateStr, taskAtRow) {
        if (!canCreate) return;
        var defaults = {};
        defaults[startField] = dateStr;
        defaults[endField] = toISODate(addDays(parseDate(dateStr), 1));
        if (taskAtRow && taskAtRow.type === 'group') {
            defaults[parentField] = taskAtRow.id;
        }
        handleCreate(defaults);
    }, [canCreate, startField, endField, parentField, handleCreate]);

    // ═════════════════════════════════════════════════════════════
    // ─── DRILLDOWN NAVIGATION ───────────────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleDrilldown = useCallback(function (task) {
        if (!detailUrl) return;
        var fieldValue = task[detailField] || task.code || task.id;
        var url = detailUrl;
        // Conditional URL (array of {link, when})
        if (Array.isArray(detailUrl)) {
            for (var i = 0; i < detailUrl.length; i++) {
                var rule = detailUrl[i];
                if (rule.when && session) {
                    var match = AntCondition.checkAll(task, rule.when, session);
                    if (match) { url = rule.link || rule.url; break; }
                } else if (!rule.when) {
                    url = rule.link || rule.url; break;
                }
            }
            if (Array.isArray(url)) return; // No match
        }
        var fullUrl = url + (url.endsWith('/') ? '' : '/') + fieldValue;
        if (navigateProp) {
            navigateProp(fullUrl);
        } else if (navigateHook) {
            navigateHook(fullUrl);
        }
    }, [detailUrl, detailField, navigateProp, navigateHook, session]);

    // ═════════════════════════════════════════════════════════════
    // ─── TASK CLICK / DOUBLE-CLICK ──────────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleTaskClick = useCallback(function (task) {
        setSelectedTaskId(task.id);
        if (onTaskClick) onTaskClick(task);
    }, [onTaskClick]);

    var handleTaskDoubleClick = useCallback(function (task) {
        if (onTaskDoubleClick) {
            onTaskDoubleClick(task);
        } else if (detailUrl) {
            handleDrilldown(task);
        }
    }, [onTaskDoubleClick, detailUrl, handleDrilldown]);

    // ═════════════════════════════════════════════════════════════
    // ─── CONTEXT MENU ───────────────────────────────────────────
    // ═════════════════════════════════════════════════════════════

    var handleContextMenu = useCallback(function (e, task) {
        e.preventDefault();
        var actions = contextActionsProp || DEFAULT_CONTEXT_ACTIONS;
        // Filter actions by editability + condition
        var filtered = actions.filter(function (a) {
            if (a.key === 'separator') return true;
            if (a.requireEditable && !editable) return false;
            if (a.key === 'edit' && !canEditTask(task)) return false;
            if (a.key === 'delete' && !canDeleteTask(task)) return false;
            if (a.key === 'create_child' && !canCreate) return false;
            if (a.visible && session) {
                return AntCondition.checkAll(task || {}, a.visible, session);
            }
            return true;
        });
        if (filtered.length === 0) return;
        setContextMenu({ x: e.clientX, y: e.clientY, task: task, actions: filtered });
    }, [contextActionsProp, editable, canCreate, canEditTask, canDeleteTask, session]);

    var handleContextAction = useCallback(function (actionKey, task) {
        setContextMenu(null);
        if (onContextAction) { onContextAction(actionKey, task); return; }

        switch (actionKey) {
            case 'view':
                if (detailUrl) handleDrilldown(task);
                break;
            case 'edit':
                if (onTaskDoubleClick) onTaskDoubleClick(task);
                else if (detailUrl) handleDrilldown(task);
                break;
            case 'create_child':
                var defaults = {};
                defaults[parentField] = task.id;
                defaults[startField] = task[startField] || task.start;
                defaults[endField] = task[endField] || task.end;
                handleCreate(defaults);
                break;
            case 'delete':
                if (canDeleteTask(task)) handleDelete(task);
                break;
            default:
                break;
        }
    }, [onContextAction, detailUrl, handleDrilldown, onTaskDoubleClick, parentField, startField, endField, handleCreate, handleDelete, canDeleteTask]);

    // Close context menu on click outside
    useEffect(function () {
        if (!contextMenu) return;
        var close = function () { setContextMenu(null); };
        window.addEventListener('click', close);
        window.addEventListener('contextmenu', close);
        return function () {
            window.removeEventListener('click', close);
            window.removeEventListener('contextmenu', close);
        };
    }, [contextMenu]);

    // ═════════════════════════════════════════════════════════════
    // ─── SCROLL SYNC (left panel ↔ timeline) ────────────────────
    // ═════════════════════════════════════════════════════════════

    useEffect(function () {
        var timeline = timelineScrollRef.current;
        var panel = taskPanelRef.current;
        if (!timeline || !panel) return;
        var panelRows = panel.querySelector('.gantt-task-rows');
        if (!panelRows) return;

        var syncing = false;
        var syncFromTimeline = function () {
            if (syncing) return;
            syncing = true;
            panelRows.scrollTop = timeline.scrollTop;
            syncing = false;
        };
        var syncFromPanel = function () {
            if (syncing) return;
            syncing = true;
            timeline.scrollTop = panelRows.scrollTop;
            syncing = false;
        };
        timeline.addEventListener('scroll', syncFromTimeline);
        panelRows.addEventListener('scroll', syncFromPanel);
        return function () {
            timeline.removeEventListener('scroll', syncFromTimeline);
            panelRows.removeEventListener('scroll', syncFromPanel);
        };
    }, []);

    // ═════════════════════════════════════════════════════════════
    // ─── ZOOM WITH SCROLL WHEEL (Ctrl+Scroll) ──────────────────
    // ═════════════════════════════════════════════════════════════

    useEffect(function () {
        var el = timelineScrollRef.current;
        if (!el) return;
        var modes = ['day', 'week', 'month', 'quarter', 'year'];
        var handleWheel = function (e) {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            var idx = modes.indexOf(mode);
            if (e.deltaY > 0 && idx < modes.length - 1) setMode(modes[idx + 1]);
            else if (e.deltaY < 0 && idx > 0) setMode(modes[idx - 1]);
        };
        el.addEventListener('wheel', handleWheel, { passive: false });
        return function () { el.removeEventListener('wheel', handleWheel); };
    }, [mode]);

    // ═════════════════════════════════════════════════════════════
    // ─── KEYBOARD SHORTCUTS ─────────────────────────────────────
    // ═════════════════════════════════════════════════════════════

    useEffect(function () {
        var handleKey = function (e) {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            var selectedTask = selectedTaskId ? rawTasks.find(function (t) { return t.id === selectedTaskId; }) : null;

            if (e.key === 'Delete' && selectedTask && canDeleteTask(selectedTask)) {
                e.preventDefault();
                handleDelete(selectedTask);
            }
            if (e.key === 'Enter' && selectedTask) {
                e.preventDefault();
                handleTaskDoubleClick(selectedTask);
            }
            if (e.key === 'Escape') {
                setSelectedTaskId(null);
                setContextMenu(null);
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                var flat = flattenTasks(rawTasks, parentField, expandedGroups);
                var idx = flat.findIndex(function (t) { return t.id === selectedTaskId; });
                if (idx < flat.length - 1) setSelectedTaskId(flat[idx + 1].id);
                else if (idx === -1 && flat.length > 0) setSelectedTaskId(flat[0].id);
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                var flat2 = flattenTasks(rawTasks, parentField, expandedGroups);
                var idx2 = flat2.findIndex(function (t) { return t.id === selectedTaskId; });
                if (idx2 > 0) setSelectedTaskId(flat2[idx2 - 1].id);
            }
        };
        window.addEventListener('keydown', handleKey);
        return function () { window.removeEventListener('keydown', handleKey); };
    }, [selectedTaskId, rawTasks, parentField, expandedGroups, canDeleteTask, handleDelete, handleTaskDoubleClick]);

    // ═════════════════════════════════════════════════════════════
    // ─── REF EXPOSE ─────────────────────────────────────────────
    // ═════════════════════════════════════════════════════════════

    useImperativeHandle(ref, function () {
        return {
            reload: loadData,
            getTasks: function () { return rawTasks; },
            setMode: setMode,
            selectTask: setSelectedTaskId,
            getSelectedTask: function () { return rawTasks.find(function (t) { return t.id === selectedTaskId; }) || null; }
        };
    });

    // ─── Flatten + Range ─────────────────────────────────────────
    var flatTasks = useMemo(function () {
        return flattenTasks(rawTasks, parentField, expandedGroups);
    }, [rawTasks, parentField, expandedGroups]);

    var range = useMemo(function () {
        if (startDateProp && endDateProp) return { start: parseDate(startDateProp), end: parseDate(endDateProp) };
        return computeRange(rawTasks, startField, endField, mode);
    }, [rawTasks, startField, endField, mode, startDateProp, endDateProp]);

    var [timelineStart, setTimelineStart] = useState(range.start);
    var [timelineEnd, setTimelineEnd] = useState(range.end);
    useEffect(function () { setTimelineStart(range.start); setTimelineEnd(range.end); }, [range]);

    // ─── Toggle group ────────────────────────────────────────────
    var handleToggleGroup = useCallback(function (groupId) {
        setExpandedGroups(function (prev) {
            var next = { ...prev };
            next[groupId] = prev[groupId] === false ? true : false;
            return next;
        });
    }, []);

    // ─── Navigation ──────────────────────────────────────────────
    var handlePrev = useCallback(function () {
        var shift = mode === 'day' ? 1 : mode === 'week' ? 7 : mode === 'month' ? 30 : mode === 'quarter' ? 90 : 365;
        setTimelineStart(function (p) { return addDays(p, -shift); });
        setTimelineEnd(function (p) { return addDays(p, -shift); });
    }, [mode]);

    var handleNext = useCallback(function () {
        var shift = mode === 'day' ? 1 : mode === 'week' ? 7 : mode === 'month' ? 30 : mode === 'quarter' ? 90 : 365;
        setTimelineStart(function (p) { return addDays(p, shift); });
        setTimelineEnd(function (p) { return addDays(p, shift); });
    }, [mode]);

    var handleToday = useCallback(function () {
        var newRange = computeRange(rawTasks, startField, endField, mode);
        setTimelineStart(newRange.start); setTimelineEnd(newRange.end);
        if (timelineScrollRef.current) {
            var today = startOfDay(new Date());
            var cfg = MODE_CONFIG[mode] || MODE_CONFIG.week;
            timelineScrollRef.current.scrollLeft = Math.max(0, diffDays(newRange.start, today) * cfg.colWidth - 200);
        }
    }, [rawTasks, startField, endField, mode]);

    var handleModeChange = useCallback(function (newMode) {
        setMode(newMode);
        var newRange = computeRange(rawTasks, startField, endField, newMode);
        setTimelineStart(newRange.start); setTimelineEnd(newRange.end);
    }, [rawTasks, startField, endField]);

    // ─── Auto-scroll on mount ────────────────────────────────────
    useEffect(function () {
        if (!timelineScrollRef.current || flatTasks.length === 0) return;
        var timer = setTimeout(function () {
            var today = startOfDay(new Date());
            var cfg = MODE_CONFIG[mode] || MODE_CONFIG.week;
            if (timelineScrollRef.current) {
                timelineScrollRef.current.scrollLeft = Math.max(0, diffDays(timelineStart, today) * cfg.colWidth - 200);
            }
        }, 80);
        return function () { clearTimeout(timer); };
    }, [flatTasks.length, timelineStart, mode]);

    // ─── Legend ───────────────────────────────────────────────────
    var legendItems = useMemo(function () {
        var used = {};
        rawTasks.forEach(function (t) { var s = t[statusField] || t.status; if (s) used[s] = true; });
        return Object.keys(used).map(function (s) { return { status: s, color: statusColors[s] || '#94A3B8' }; });
    }, [rawTasks, statusField, statusColors]);

    // ─── Toolbar visibility ──────────────────────────────────────
    var showCreateBtn = canCreate && (toolbarConfig ? toolbarConfig.showCreate !== false : true);

    // ═════════════════════════════════════════════════════════════
    // ─── RENDER ─────────────────────────────────────────────────
    // ═════════════════════════════════════════════════════════════
    if (isHidden) return null;
    return (
        <div className={'ant-gantt-chart' + (compactRows ? ' compact' : '')} style={{ height: height }}>
            {/* ─── Navigation bar ─────────────────────────────────── */}
            <div className="gantt-nav">
                <div className="gantt-nav-left">
                    <button className="gantt-nav-btn" onClick={handlePrev} title={t({ vi: 'Trước', en: 'Previous', fr: 'Précédent', es: 'Anterior', id: 'Sebelumnya', ja: '前へ', zh: '上一个', ko: '이전', hi: 'पिछला', ar: 'السابق' })}>◀</button>
                    <button className="gantt-nav-btn today-btn" onClick={handleToday}>{t({ vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' })}</button>
                    <button className="gantt-nav-btn" onClick={handleNext} title={t({ vi: 'Sau', en: 'Next', fr: 'Suivant', es: 'Próximo', id: 'Berikut', ja: '次へ', zh: '下一个', ko: '다음', hi: 'अगला', ar: 'التالي' })}>▶</button>
                    {showCreateBtn && (
                        <button
                            className="gantt-nav-btn create-btn"
                            onClick={function () { handleCreate({}); }}
                            title={t({ vi: 'Tạo mới', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएं', ar: 'إنشاء' })}
                        >
                            + {t({ vi: 'Tạo', en: 'New', fr: 'Créer', es: 'Nuevo', id: 'Buat', ja: '作成', zh: '新建', ko: '생성', hi: 'नया', ar: 'جديد' })}
                        </button>
                    )}
                </div>
                <div className="gantt-nav-center">
                    {loading && <span className="gantt-loading">{t({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</span>}
                    <span className="gantt-task-count">{flatTasks.length} {t({ vi: 'công việc', en: 'tasks', fr: 'tâches', es: 'tareas', id: 'tugas', ja: '件のタスク', zh: '个任务', ko: '개 작업', hi: 'कार्य', ar: 'مهام' })}</span>
                </div>
                <div className="gantt-nav-right">
                    <div className="gantt-mode-group">
                        {MODE_LABELS.map(function (m) {
                            return (
                                <button
                                    key={m.key}
                                    className={'gantt-mode-btn' + (mode === m.key ? ' active' : '')}
                                    onClick={function () { handleModeChange(m.key); }}
                                    title={t(m.title)}
                                >
                                    {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ─── Body ───────────────────────────────────────────── */}
            <div className="gantt-body">
                <div ref={taskPanelRef}>
                    <GanttTaskPanel
                        tasks={flatTasks}
                        expandedGroups={expandedGroups}
                        onToggleGroup={handleToggleGroup}
                        onTaskClick={handleTaskClick}
                        onTaskContextMenu={handleContextMenu}
                        nameField={nameField}
                        assigneeField={assigneeField}
                        startField={startField}
                        endField={endField}
                        statusField={statusField}
                        rowHeight={rowHeight}
                        compact={compactRows}
                        selectedTaskId={selectedTaskId}
                        session={session}
                        columns={panelColumns}
                        editable={editable}
                    />
                </div>
                <div className="gantt-timeline-scroll" ref={timelineScrollRef}>
                    <GanttTimeline
                        tasks={flatTasks}
                        timelineStart={timelineStart}
                        timelineEnd={timelineEnd}
                        mode={mode}
                        rowHeight={rowHeight}
                        statusColors={statusColors}
                        showProgress={showProgress}
                        showDependencies={showDependencies}
                        showMilestones={showMilestones}
                        showToday={showToday}
                        nameField={nameField}
                        startField={startField}
                        endField={endField}
                        progressField={progressField}
                        statusField={statusField}
                        assigneeField={assigneeField}
                        idField={idField}
                        onTaskClick={handleTaskClick}
                        onTaskDoubleClick={handleTaskDoubleClick}
                        onTaskContextMenu={handleContextMenu}
                        onEmptySlotClick={handleEmptySlotClick}
                        onDragMove={handleDragMove}
                        onDragResize={handleDragResize}
                        editable={editable}
                        compact={compactRows}
                    />
                </div>
            </div>

            {/* ─── Legend ──────────────────────────────────────────── */}
            <div className="gantt-legend">
                {legendItems.map(function (item) {
                    return (
                        <span key={item.status} className="legend-item">
                            <span className="legend-dot" style={{ background: item.color }} />{item.status}
                        </span>
                    );
                })}
                {showMilestones && <span className="legend-item"><span className="legend-diamond">◆</span>{t({ vi: 'Mốc', en: 'Milestone', fr: 'Jalon', es: 'Hito', id: 'Tonggak', ja: '節目', zh: '里程碑', ko: '마일스톤', hi: 'माइलस्टोन', ar: 'مرحلة' })}</span>}
                {showDependencies && <span className="legend-item"><span className="legend-arrow">─→</span>{t({ vi: 'Phụ thuộc', en: 'Dependency', fr: 'Dépendance', es: 'Dependencia', id: 'Dependensi', ja: '依存関係', zh: '依赖', ko: '의존 관계', hi: 'निर्भरता', ar: 'تبعية' })}</span>}
                {editable && <span className="legend-item legend-hint">{t({ vi: 'Ctrl+Scroll = Zoom · Nhấp đúp = Mở · Chuột phải = Menu', en: 'Ctrl+Scroll = Zoom · Double-click = Open · Right-click = Menu', fr: 'Ctrl+Molette = Zoom · Double-clic = Ouvrir · Clic droit = Menu', es: 'Ctrl+Rueda = Zoom · Doble clic = Abrir · Clic derecho = Menú', id: 'Ctrl+Scroll = Zoom · Klik ganda = Buka · Klik kanan = Menu', ja: 'Ctrl+スクロール = ズーム · ダブルクリック = 開く · 右クリック = メニュー', zh: 'Ctrl+滚轮 = 缩放 · 双击 = 打开 · 右键 = 菜单', ko: 'Ctrl+스크롤 = 확대/축소 · 더블클릭 = 열기 · 우클릭 = 메뉴', hi: 'Ctrl+स्क्रॉल = ज़ूम · डबल-क्लिक = खोलें · राइट-क्लिक = मेनू', ar: 'Ctrl+تمرير = تكبير · نقر مزدوج = فتح · زر أيمن = قائمة' })}</span>}
            </div>

            {/* ─── Context Menu ────────────────────────────────────── */}
            {contextMenu && (
                <div className="gantt-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
                    {contextMenu.actions.map(function (action, i) {
                        if (action.key === 'separator') return <div key={i} className="context-separator" />;
                        return (
                            <div
                                key={action.key}
                                className={'context-item' + (action.danger ? ' danger' : '')}
                                onClick={function (e) { e.stopPropagation(); handleContextAction(action.key, contextMenu.task); }}
                            >
                                {action.icon && <i className={action.icon} />}
                                <span>{typeof action.label === 'object' ? t(action.label) : action.label}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});
